/**
 * Todos os textos que o chat mostra, num só sítio (PT).
 *
 * As chaves `t-*` do ngx-translate vivem no i18n do back-end e o chat
 * redesenhado tem muitos textos que lá não existem, por isso a página traz a
 * sua própria cópia, como a biblioteca da PWA. Copiado de
 * `imodigi-chat/lib/chat.strings.ts` sem o InjectionToken (o CRM não o precisa)
 * e com os textos do papel do CRM: separadores "Todas / Não lidas / Equipa / Por
 * atribuir", faixa "sem resposta" e painel "Atribuir a".
 *
 * `{name}` / `{by}` nos textos `system` são substituídos pelos `system.params`
 * da mensagem (contrato 6.2).
 */
export interface ChatStrings {
  /* lista */
  listTitle: string;
  searchPlaceholder: string;
  searchLabel: string;
  tabTeam: string;
  emptyListTitle: string;
  emptyListText: string;
  emptyTeamTitle: string;
  emptyTeamText: string;
  pickConversationTitle: string;
  pickConversationText: string;
  attachmentPreview: string;
  teamTag: string;

  /* ligação */
  loading: string;
  offline: string;
  sessionExpired: string;
  invalidCredentials: string;
  temporarilyUnavailable: string;
  noChatIdentityTitle: string;
  noChatIdentityText: string;

  /* cabeçalho */
  more: string;
  invite: string;
  openProperty: string;
  openPropertySite: string;
  roleClient: string;
  roleAgent: string;
  roleStaff: string;
  presenceOnline: string;
  presenceAway: string;
  presenceOffline: string;
  ownerLabel: string;

  /* mensagens */
  today: string;
  yesterday: string;
  weekdays: string[];
  read: string;
  delivered: string;
  sending: string;
  failed: string;
  retry: string;
  autoReply: string;
  deleteMessage: string;
  deleteFile: string;
  openImage: string;
  pdf: string;
  system: { [code: string]: string };
  /** Ícone da linha de sistema por `code` (nome de chat-icon). */
  systemIcon: { [code: string]: string };

  /* compositor */
  moreActions: string;
  tabNote: string;
  placeholderMessage: string;
  placeholderNote: string;
  attach: string;
  sendProperty: string;
  send: string;
  supervisedReadOnly: string;
  supervisedTakeOver: string;

  /* atribuição (CRM) */
  unansweredFor: string;
  unansweredNoOwner: string;
  takeOver: string;
  assignToConsultant: string;
  assignTitle: string;
  assignSubtitle: string;
  assign: string;
  assignEmpty: string;
  closePanel: string;
  agoMinutes: string;
  agoHours: string;
  agoDays: string;
  justNow: string;

  /* diálogos */
  confirmTitle: string;
  confirmText: string;
  confirmDelete: string;
  cancel: string;
  close: string;
  deletedTitle: string;
  deletedChat: string;
  deletedMessage: string;
  deletedFile: string;
  errorTitle: string;
  filesTooMany: string;
  filesBadType: string;
  fileTooBig: string;
  filesTooBig: string;
  filesFailed: string;
  /** 415 do envio: o servidor não leu o ficheiro como imagem nem PDF. */
  filesUnreadable: string;
  /** Linha de sistema sem nome conhecido: "alguém" em vez de um id. */
  systemSomeone: string;
  fileDeleteFailed: string;
  messageDeleteFailed: string;

  /* versão 2: fila, presença, convite, imóveis, respostas rápidas, definições */
  tabQueue: string;
  emptyQueueTitle: string;
  emptyQueueText: string;
  settings: string;
  presenceAvailable: string;
  presenceAwayShort: string;
  presenceFailed: string;
  typing: string;
  withParticipants: string;
  assignedLabel: string;
  unassigned: string;
  assignFailed: string;
  inviteTitle: string;
  inviteText: string;
  invitePlaceholder: string;
  inviteEmpty: string;
  inviteConfirm: string;
  inviteFailed: string;
  /* convites por aceitar (contrato 6.3, v8) */
  invitesTitle: string;
  /* lista: duas vistas para quem supervisiona (2026-10-26) */
  viewMine: string;
  viewSupervision: string;
  tabColleagues: string;
  filterWaiting: string;
  groupConversations: string;
  groupWaiting: string;
  noOwnerGroup: string;
  waitingTag: string;
  newColleagueConversation: string;
  emptyWaitingTitle: string;
  emptyWaitingText: string;
  /* 2026-10-27: filtros em cima, separadores em baixo, Supervisão paginada */
  filtersLabel: string;
  segmentStaffSupervision: string;
  supervisionClientsHint: string;
  supervisionStaffHint: string;
  loadMoreConsultants: string;
  showMoreConversations: string;
  supervisionFailed: string;
  supervisionRetry: string;
  emptySupervisionStaffTitle: string;
  emptySupervisionStaffText: string;
  /** "Apartamento ou Moradia", "T1 ou T2" (qualificação com várias escolhas, 7.2 v8). */
  qualificationOr: string;
  qualificationMultiHint: string;
  inviteFrom: string;
  inviteFromNoClient: string;
  inviteAccept: string;
  inviteDecline: string;
  inviteSent: string;
  inviteDeclined: string;
  inviteGone: string;
  invitesPending: string;
  inviteCancel: string;
  inviteWaiting: string;
  launcherInvite: string;
  sendPropertyTitle: string;
  propertySearchPlaceholder: string;
  propertySearchLabel: string;
  propertyEmpty: string;
  propertySelected: string;
  propertySelectedOne: string;
  propertyNoneSelected: string;
  sendProperties: string;
  sendPropertyOne: string;
  quickReplies: string;
  quickRepliesEmpty: string;
  quickNoMatch: string;

  /* definições (chat/settings) */
  settingsTitle: string;
  backToChat: string;
  autoRepliesTitle: string;
  autoRepliesText: string;
  autoRepliesGeneral: string;
  autoRepliesMine: string;
  autoRepliesReadOnly: string;
  triggers: { [trigger: string]: string };
  triggerHints: { [trigger: string]: string };
  messageLabel: string;
  activeLabel: string;
  save: string;
  saved: string;
  saving: string;
  saveFailed: string;
  quickRepliesTitle: string;
  quickRepliesText: string;
  newQuickReply: string;
  editQuickReply: string;
  shortcutLabel: string;
  shortcutHint: string;
  generalLabel: string;
  generalTag: string;
  inactiveTag: string;
  edit: string;
  delete: string;
  deleteFailed: string;
  noQuickReplies: string;
  create: string;

  /* versão 3: apresentação (lista, fio, compositor, emojis, lightbox) */
  clearSearch: string;
  timeNow: string;
  timeMinutes: string;
  attachmentsPreview: string;
  backToList: string;
  unreadSeparator: string;
  newMessages: string;
  newMessagesCount: string;
  scrollToLatest: string;
  imageOf: string;
  previousImage: string;
  nextImage: string;
  openPdf: string;
  emoji: string;
  emojiSearch: string;
  emojiEmpty: string;
  emojiRecent: string;
  emojiCategories: { [id: string]: string };
  removeAttachment: string;
  attachmentsSelected: string;
  attachmentSelectedOne: string;
  charactersLeft: string;
  sendHint: string;
  savedToast: string;
  deletedToast: string;
  triggerHelp: string;

  /* versão 3b: estrutura da referência (detalhes, ações rápidas, pesquisa na conversa) */
  tabAll: string;
  tabUnread: string;
  emptyUnreadTitle: string;
  emptyUnreadText: string;
  emptySearchTitle: string;
  details: string;
  searchInConversation: string;
  searchInConversationPlaceholder: string;
  searchHits: string;
  searchNoHits: string;
  previousHit: string;
  nextHit: string;
  scheduleVisit: string;
  sendProposal: string;
  sendImage: string;
  exitNoteMode: string;
  onlineNow: string;
  infoTitle: string;
  /* painel de detalhes (v3): cliente, lead, negócio */
  clientTitle: string;
  emailLabel: string;
  phoneLabel: string;
  sourceLabel: string;
  budgetLabel: string;
  scoreLabel: string;
  temperatureHot: string;
  temperatureWarm: string;
  temperatureCold: string;
  /** Dica do ícone de temperatura na lista: "Quente · score 82". */
  temperatureTip: string;
  dealTitle: string;
  dealWon: string;
  dealLost: string;
  openDeal: string;
  noDealYet: string;
  /** Legenda das variáveis nas definições. */
  variablesHint: string;
  /** Balão flutuante: "abrir a página do chat". */
  launcherOpenPage: string;
  /* escolher UM imóvel no seletor (visita / horários / proposta) */
  confirmPropertyChoose: string;
  usePropertyLabel: string;
  /** Rodapé do seletor de imóveis quando se escolhe o imóvel de uma visita / proposta. */
  confirmPropertyPickHint: string;
  leaveChat: string;
  leaveChatConfirm: string;
  removeParticipant: string;
  leftToast: string;
  participantsFailed: string;
  /* botões do painel de detalhes, com texto */
  openPropertyShort: string;
  openSiteShort: string;
  inviteShort: string;
  transferShort: string;
  qualificationFreeText: string;
  /* rodapé da lista: Clientes / Consultores; nova conversa entre colegas */
  segmentClients: string;
  segmentStaff: string;
  /* seletor de colegas (2026-10-28): loja / rede, filtro por agência */
  colleagueScopeLabel: string;
  colleagueScopeStore: string;
  colleagueScopeNetwork: string;
  colleagueStoreAll: string;
  colleagueSearch: string;
  colleagueEmpty: string;
  newConversation: string;
  newConversationTitle: string;
  newConversationText: string;
  newConversationPlaceholder: string;
  newConversationStart: string;
  emptyStaffTitle: string;
  emptyStaffText: string;
  /* visitas (7.3) */
  scheduleVisitTitle: string;
  proposeSlots: string;
  proposeSlotsTitle: string;
  visitPropertyLabel: string;
  changeProperty: string;
  noVisitProperty: string;
  quickPicksLabel: string;
  tomorrow: string;
  conflictWith: string;
  dateLabel: string;
  timeLabel: string;
  durationLabel: string;
  minutesShort: string;
  sendClientEmail: string;
  slotsHint: string;
  /* 2026-10-27: seletor de imóveis com filtros e "Agendar visita" único */
  pickerFilters: string;
  pickerScopeLabel: string;
  pickerMine: string;
  pickerMyAgency: string;
  pickerNetwork: string;
  pickerDistrict: string;
  pickerCounty: string;
  pickerCountyHint: string;
  pickerPropertyType: string;
  pickerBusiness: string;
  pickerTypology: string;
  pickerPriceMin: string;
  pickerPriceMax: string;
  pickerFromRequest: string;
  pickerRemoveTag: string;
  pickerClear: string;
  pickerOnlyAvailable: string;
  pickerShowMore: string;
  scheduleModeLabel: string;
  scheduleModeNow: string;
  scheduleModeAsk: string;
  askAvailabilityNotice: string;
  nextStepVisitForm: string;
  addSlot: string;
  removeSlot: string;
  noteLabel: string;
  confirmSchedule: string;
  sendRequest: string;
  visitScheduledToast: string;
  visitCanceledToast: string;
  requestSentToast: string;
  visitFailed: string;
  announceFailed: string;
  qualificationFailed: string;
  dealFailed: string;
  reminderFailed: string;
  formFailed: string;
  visitRequestTitle: string;
  visitRequestText: string;
  visitRequestPending: string;
  visitRequestChosen: string;
  visitRequestProposed: string;
  visitRequestDeclined: string;
  visitRequestScheduled: string;
  proposeOther: string;
  proposeConfirm: string;
  declineRequest: string;
  confirmVisitFromChoice: string;
  acceptProposal: string;
  scheduleAnyway: string;
  visitRequestConflict: string;
  visitRequestScheduling: string;
  visitCardTitle: string;
  visitStatus: { [code: string]: string };
  visitCancel: string;
  visitCancelReason: string;
  visitCancelConfirm: string;
  visitAddToCalendar: string;
  /* secção 12: reações, apagar para mim, enviar mais tarde */
  react: string;
  reactionFailed: string;
  /* mensagens de voz */
  voiceMessage: string;
  recordingVoice: string;
  recordVoice: string;
  recording: string;
  cancelRecording: string;
  sendRecording: string;
  micDenied: string;
  voicePreview: string;
  voiceFailed: string;
  play: string;
  pause: string;
  playbackSpeed: string;
  deleteTitle: string;
  deleteChoiceText: string;
  deleteForMeText: string;
  deleteForEveryone: string;
  deleteForMe: string;
  scheduleSend: string;
  scheduleTitle: string;
  scheduleConfirm: string;
  scheduleCustom: string;
  scheduledCount: string;
  scheduledOne: string;
  scheduledAt: string;
  cancelScheduled: string;
  scheduledToast: string;
  scheduledCanceledToast: string;
  scheduledFailed: string;
  scheduleFailed: string;
  /* próximo passo (fase 3) */
  nextStepConfirmVisit: string;
  nextStepAcceptProposal: string;
  nextStepMatching: string;
  nextStepMatchingOne: string;
  nextStepAnswered: string;
  nextStepQualify: string;
  nextStepPropose: string;
  nextStepEvaluate: string;
  nextStepProposal: string;
  nextStepSeeAndSend: string;
  nextStepSendProperties: string;
  nextStepDismiss: string;
  rescheduleVisit: string;
  rescheduleVisitTitle: string;
  confirmReschedule: string;
  visitRescheduledToast: string;
  visitRescheduled: string;
  visitRequestForm: string;
  visitFormTitle: string;
  visitFormPending: string;
  visitFormAnswered: string;
  visitFormOpen: string;
  visitFormCopyLink: string;
  linkCopied: string;
  requestFormTitle: string;
  requestFormText: string;
  requestFormSend: string;
  noVisits: string;
  formRequested: string;
  formAnsweredShort: string;
  formSentToast: string;
  /** Pré-visualização na lista por tipo de mensagem. */
  previewByKind: { [kind: string]: string };
  /* qualificação (7.3) */
  qualify: string;
  qualificationTitle: string;
  qualificationText: string;
  qualificationPending: string;
  qualificationAnswered: string;
  qualificationSend: string;
  qualificationFieldsHint: string;
  qualificationField: { [key: string]: string };
  qualificationEdit: string;
  qualificationSearch: string;
  qualificationSave: string;
  qualificationSentToast: string;
  qualificationSavedToast: string;
  budgetMinLabel: string;
  budgetMaxLabel: string;
  bedroomsLabel: string;
  notesLabel: string;
  notesPlaceholder: string;
  propertyTypeLabel: string;
  propertyTypePlaceholder: string;
  businessTypeLabel: string;
  qualAnswerForClient: string;
  qualEditAnswers: string;
  qualAnswerMyself: string;
  qualPreviewHint: string;
  qualPreviewEmpty: string;
  /** O que cada pergunta oferece ao cliente (pré-visualização ao enviar). */
  qualPreviewByField: { [key: string]: string };
  qualAnswersHint: string;
  budgetUpTo: string;
  /* proposta (7.3) */
  proposalCardTitle: string;
  openProposal: string;
  proposalSharedToast: string;
  proposalNotFound: string;
  /* negócio e lembretes (7.3) */
  advanceStageTo: string;
  stageAdvancedToast: string;
  stageFailed: string;
  nextActionLabel: string;
  noNextAction: string;
  addReminder: string;
  reminderTitle: string;
  reminderNote: string;
  reminderSave: string;
  reminderSavedToast: string;
  nextActionVisit: string;
  /* transferir (7.3) */
  transferTitle: string;
  transferText: string;
  transferPlaceholder: string;
  transferNote: string;
  transferConfirm: string;
  transferFailed: string;
  transferredToast: string;
  /* menções (7.1) */
  mentionsLabel: string;
  roleLabel: string;
  responsibleLabel: string;
  propertyLabel: string;
  stateLabel: string;
  colleaguesTitle: string;
  participantResponsible: string;
  participantInvited: string;
  participantYou: string;
  inviteColleague: string;
  conversationPropertyTitle: string;
  sentPropertiesTitle: string;
  noColleagues: string;
  noteAddedBy: string;
  noteVisibility: string;
  seeDetails: string;
  /* cartão de imóvel (v3) */
  priceOnRequest: string;
  forSale: string;
  forRent: string;
  /** "{n} WC" */
  bathroomsShort: string;
  /** Estado comercial por `businessStatus.code`; sem entrada usa-se o nome do servidor. */
  propertyStatus: { [code: string]: string };
  /* menu do utilizador no topo da lista */
  myStatus: string;
  presenceAvailableHint: string;
  presenceAwayHint: string;
  /* aviso de mensagem nova no balão flutuante */
  launcherNewConversation: string;
  launcherPeekOpen: string;
  launcherPeekDismiss: string;
  /* editar e apagar para todos (secção 9, regras do WhatsApp) */
  editMessage: string;
  editingMessage: string;
  cancelEdit: string;
  edited: string;
  editExpired: string;
  editFailed: string;
  messageDeleted: string;
  messageDeletedMine: string;
  noteDeleted: string;
  messageDeletedPreview: string;
  deleteForEveryoneTitle: string;
  deleteForEveryoneText: string;
  deleteExpired: string;
  /* responder a uma mensagem (secção 10) */
  reply: string;
  replyingTo: string;
  cancelReply: string;
  replyQuoteHint: string;
}

export const CHAT_STRINGS_PT: ChatStrings = {
  listTitle: 'Conversas',
  searchPlaceholder: 'Procurar por nome, imóvel ou referência',
  searchLabel: 'Procurar conversas',
  tabTeam: 'Equipa',
  emptyListTitle: 'Ainda não tem conversas',
  emptyListText: 'As conversas dos clientes do site e da app aparecem aqui. As conversas com colegas ficam em Consultores.',
  emptyTeamTitle: 'Sem conversas da equipa',
  emptyTeamText: 'As conversas dos clientes da sua equipa aparecem aqui, agrupadas por consultor.',
  pickConversationTitle: 'Escolha uma conversa',
  pickConversationText: 'As mensagens aparecem aqui.',
  attachmentPreview: 'Anexo',
  teamTag: 'Equipa',

  loading: 'A carregar…',
  offline: 'Sem ligação. As mensagens seguem quando voltar.',
  sessionExpired: 'A sessão expirou. A voltar a ligar…',
  invalidCredentials: 'Precisa de iniciar sessão de novo para usar o chat.',
  temporarilyUnavailable: 'O chat está temporariamente indisponível. Vamos tentar de novo dentro de momentos.',
  noChatIdentityTitle: 'Sem conta de chat',
  noChatIdentityText: 'A sua conta não tem chat nesta entidade. Peça a um administrador para o ativar.',

  more: 'Mais opções',
  invite: 'Convidar colega',
  openProperty: 'Abrir ficha',
  openPropertySite: 'Ver no site',
  roleClient: 'Cliente',
  roleAgent: 'Consultor',
  roleStaff: 'Equipa',
  presenceOnline: 'Online agora',
  presenceAway: 'ausente, responde mais tarde',
  presenceOffline: '',
  ownerLabel: 'consultor: {name}',

  today: 'Hoje',
  yesterday: 'Ontem',
  weekdays: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
  read: 'Lida',
  delivered: 'Entregue',
  sending: 'A enviar',
  failed: 'Não foi enviada',
  retry: 'Tentar de novo',
  autoReply: 'Resposta automática',
  deleteMessage: 'Apagar',
  deleteFile: 'Apagar ficheiro',
  openImage: 'Abrir imagem',
  pdf: 'PDF',
  system: {
    assigned: '{byName} atribuiu a conversa a {name}',
    /** Quem já participava assumiu a conversa (by === userAppId); sem participar é `joined`. */
    assignedSelf: '{name} assumiu a conversa',
    invited: '{byName} convidou {name} para a conversa',
    joined: '{name} entrou na conversa',
    left: '{name} saiu da conversa',
    removed: '{byName} retirou {name} da conversa',
    transferred: '{byName} transferiu a conversa para {name}'
  },
  systemIcon: {
    assigned: 'users',
    invited: 'userplus',
    joined: 'userplus',
    left: 'close',
    removed: 'close',
    transferred: 'transfer'
  },

  moreActions: 'Mais ações',
  tabNote: 'Nota interna',
  placeholderMessage: 'Escreva uma mensagem',
  placeholderNote: 'Nota que só a equipa vê',
  attach: 'Anexar ficheiro',
  sendProperty: 'Enviar imóvel',
  send: 'Enviar',
  supervisedReadOnly: 'Conversa de {name}. Está a acompanhar em modo de leitura.',
  supervisedTakeOver: 'Assumir a conversa',

  unansweredFor: 'Esta conversa está {time} sem resposta.',
  unansweredNoOwner: 'Esta conversa está {time} sem resposta e sem responsável.',
  takeOver: 'Assumir eu',
  assignToConsultant: 'Atribuir a consultor',
  assignTitle: 'Atribuir a',
  assignSubtitle: 'Consultores associados a si',
  assign: 'Atribuir',
  assignEmpty: 'Nenhum consultor com chat na sua equipa.',
  closePanel: 'Fechar painel',
  agoMinutes: 'há {n} min',
  agoHours: 'há {n} h',
  agoDays: 'há {n} dias',
  justNow: 'agora mesmo',

  confirmTitle: 'Tem a certeza?',
  confirmText: 'Esta ação não pode ser anulada.',
  confirmDelete: 'Apagar',
  cancel: 'Cancelar',
  close: 'Fechar',
  deletedTitle: 'Apagado',
  deletedChat: 'A conversa foi apagada.',
  deletedMessage: 'A mensagem foi apagada.',
  deletedFile: 'O ficheiro foi apagado.',
  errorTitle: 'Algo correu mal',
  filesTooMany: 'Só pode enviar 20 ficheiros de cada vez.',
  filesBadType: 'Só pode enviar imagens (JPEG, PNG, GIF, WebP) e PDF.',
  fileTooBig: 'Cada ficheiro pode ter no máximo 5 MB.',
  filesTooBig: 'Os ficheiros de uma mensagem podem ter no máximo 25 MB no total.',
  filesFailed: 'Os ficheiros não foram enviados.',
  filesUnreadable: 'Não foi possível ler o ficheiro. Envie uma imagem ou um PDF válido.',
  systemSomeone: 'alguém',
  fileDeleteFailed: 'Não foi possível apagar o ficheiro.',
  messageDeleteFailed: 'Não foi possível apagar a mensagem.',

  tabQueue: 'Por atribuir',
  emptyQueueTitle: 'Sem conversas por atribuir',
  emptyQueueText: 'As conversas de clientes sem responsável aparecem aqui, as mais antigas primeiro.',
  settings: 'Definições do chat',
  presenceAvailable: 'Disponível',
  presenceAwayShort: 'Ausente',
  presenceFailed: 'Não foi possível mudar o estado.',
  typing: 'a escrever…',
  withParticipants: 'com {names}',
  assignedLabel: 'responsável: {name}',
  unassigned: 'Esta conversa não tem responsável.',
  assignFailed: 'Não foi possível atribuir a conversa.',
  inviteTitle: 'Convidar colega',
  inviteText: 'O colega recebe um convite e só vê a conversa depois de aceitar.',
  invitePlaceholder: 'Escolha um colega',
  inviteEmpty: 'Nenhum colega com chat para convidar.',
  inviteConfirm: 'Convidar',
  inviteFailed: 'Não foi possível convidar o colega.',
  invitesTitle: 'Convites',
  viewMine: 'As minhas',
  viewSupervision: 'Supervisão',
  tabColleagues: 'Colegas',
  filterWaiting: 'À espera de resposta',
  groupConversations: '{n} conversas',
  groupWaiting: '{n} à espera',
  noOwnerGroup: 'Sem responsável',
  waitingTag: 'à espera há {time}',
  newColleagueConversation: 'Nova conversa com um colega',
  emptyWaitingTitle: 'Ninguém à espera',
  emptyWaitingText: 'Todos os clientes da equipa já têm resposta.',
  filtersLabel: 'Filtros',
  segmentStaffSupervision: 'Entre consultores',
  supervisionClientsHint: 'Conversas da equipa com clientes, agrupadas por responsável.',
  supervisionStaffHint: 'Conversas internas entre consultores da equipa. Só de leitura.',
  loadMoreConsultants: 'Carregar mais consultores',
  showMoreConversations: 'Ver mais conversas',
  supervisionFailed: 'Não foi possível carregar a supervisão.',
  supervisionRetry: 'Tentar de novo',
  emptySupervisionStaffTitle: 'Sem conversas entre consultores',
  emptySupervisionStaffText: 'As conversas internas da equipa aparecem aqui, agrupadas por consultor.',
  qualificationOr: 'ou',
  qualificationMultiHint: 'Pode escolher mais do que um.',
  inviteFrom: '{by} convidou-o para a conversa com {client}',
  inviteFromNoClient: '{by} convidou-o para uma conversa',
  inviteAccept: 'Aceitar',
  inviteDecline: 'Recusar',
  inviteSent: 'Convite enviado a {name}.',
  inviteDeclined: '{name} recusou o convite.',
  inviteGone: 'Este convite já não está disponível.',
  invitesPending: 'Convites por aceitar',
  inviteCancel: 'Cancelar convite',
  inviteWaiting: 'à espera de resposta · {time}',
  launcherInvite: 'Convite para uma conversa',
  sendPropertyTitle: 'Enviar imóvel a {name}',
  propertySearchPlaceholder: 'Procurar por referência ou título',
  propertySearchLabel: 'Procurar imóveis',
  propertyEmpty: 'Nenhum imóvel encontrado.',
  propertySelected: '{n} imóveis selecionados. Cada um segue como um cartão.',
  propertySelectedOne: '1 imóvel selecionado. Segue como um cartão.',
  propertyNoneSelected: 'Escolha um ou mais imóveis.',
  sendProperties: 'Enviar {n} imóveis',
  sendPropertyOne: 'Enviar 1 imóvel',
  quickReplies: 'Respostas rápidas',
  quickRepliesEmpty: 'Sem respostas rápidas. Crie-as nas definições do chat.',
  quickNoMatch: 'Nenhuma resposta rápida com esse atalho.',

  settingsTitle: 'Definições do chat',
  backToChat: 'Voltar ao chat',
  autoRepliesTitle: 'Respostas automáticas',
  autoRepliesText: 'O cliente vê-as marcadas como resposta automática. As gerais valem para toda a agência; as suas valem só para as suas conversas e ganham às gerais.',
  autoRepliesGeneral: 'Gerais da agência',
  autoRepliesMine: 'As minhas',
  autoRepliesReadOnly: 'Só a direção altera as respostas gerais.',
  triggers: {
    first_contact: 'Primeiro contacto',
    out_of_hours: 'Fora de horas',
    away: 'Consultor ausente',
    unanswered: 'Sem resposta'
  },
  triggerHints: {
    first_contact: 'Quando um cliente abre uma conversa nova',
    out_of_hours: 'Fora do horário da agência (ainda não disponível no servidor)',
    away: 'Quando o consultor está marcado como ausente',
    unanswered: 'Quando ninguém respondeu e a conversa vai para o diretor (ainda não disponível no servidor)'
  },
  messageLabel: 'Mensagem',
  activeLabel: 'Ativa',
  save: 'Guardar',
  saved: 'Guardado',
  saving: 'A guardar…',
  saveFailed: 'Não foi possível guardar.',
  quickRepliesTitle: 'Respostas rápidas',
  quickRepliesText: 'No compositor, escreva o atalho (por exemplo /visita) para inserir a mensagem.',
  newQuickReply: 'Nova resposta rápida',
  editQuickReply: 'Editar resposta rápida',
  shortcutLabel: 'Atalho',
  shortcutHint: 'Comece por / e use só letras minúsculas, números, _ ou - (máx. 40 caracteres).',
  generalLabel: 'Geral (toda a agência)',
  generalTag: 'Geral',
  inactiveTag: 'Inativa',
  edit: 'Editar',
  delete: 'Apagar',
  deleteFailed: 'Não foi possível apagar.',
  noQuickReplies: 'Ainda não há respostas rápidas.',
  create: 'Criar',

  clearSearch: 'Limpar pesquisa',
  timeNow: 'agora',
  timeMinutes: '{n} min',
  attachmentsPreview: '{n} anexos',
  backToList: 'Voltar à lista',
  unreadSeparator: 'Não lidas',
  newMessages: 'Novas mensagens',
  newMessagesCount: '{n} novas mensagens',
  scrollToLatest: 'Ir para a última mensagem',
  imageOf: 'Imagem {i} de {n}',
  previousImage: 'Imagem anterior',
  nextImage: 'Imagem seguinte',
  openPdf: 'Abrir PDF',
  emoji: 'Emojis',
  emojiSearch: 'Procurar emoji',
  emojiEmpty: 'Nenhum emoji com esse nome.',
  emojiRecent: 'Recentes',
  emojiCategories: {
    smileys: 'Sorrisos',
    gestures: 'Gestos e pessoas',
    home: 'Casa e imobiliário',
    objects: 'Objetos',
    symbols: 'Símbolos'
  },
  removeAttachment: 'Remover anexo',
  attachmentsSelected: '{n} anexos por enviar',
  attachmentSelectedOne: '1 anexo por enviar',
  charactersLeft: '{n} caracteres restantes',
  sendHint: 'Enter envia · Shift+Enter muda de linha',
  savedToast: 'Definições guardadas.',
  deletedToast: 'Resposta rápida apagada.',
  triggerHelp: 'Quando dispara',

  tabAll: 'Todas',
  tabUnread: 'Não lidas',
  emptyUnreadTitle: 'Tudo lido',
  emptyUnreadText: 'Não tem mensagens por ler.',
  emptySearchTitle: 'Nenhuma conversa encontrada',
  details: 'Detalhes',
  searchInConversation: 'Pesquisar na conversa',
  searchInConversationPlaceholder: 'Procurar nas mensagens',
  searchHits: '{i} de {n}',
  searchNoHits: 'Sem resultados',
  previousHit: 'Resultado anterior',
  nextHit: 'Resultado seguinte',
  scheduleVisit: 'Agendar visita',
  sendProposal: 'Enviar proposta',
  sendImage: 'Enviar imagem',
  exitNoteMode: 'Voltar a responder',
  onlineNow: 'Online agora',
  infoTitle: 'Informações',
  clientTitle: 'Cliente',
  emailLabel: 'Email',
  phoneLabel: 'Telefone',
  sourceLabel: 'Origem',
  budgetLabel: 'Orçamento',
  scoreLabel: 'Pontuação',
  temperatureHot: 'Quente',
  temperatureWarm: 'Morno',
  temperatureCold: 'Frio',
  temperatureTip: '{label} · score {score}',
  dealTitle: 'Negócio',
  dealWon: 'Ganho',
  dealLost: 'Perdido',
  openDeal: 'Abrir negócio',
  noDealYet: 'Ainda sem negócio. É criado ao agendar uma visita ou registar uma proposta.',
  variablesHint: 'Pode usar {{nome}}, {{imovel}}, {{preco}}, {{consultor}} e {{agencia}}: são substituídos pelos dados da conversa.',
  launcherOpenPage: 'Abrir a página do chat',
  confirmPropertyChoose: 'Escolher imóvel',
  usePropertyLabel: 'Usar este imóvel',
  confirmPropertyPickHint: 'Escolha o imóvel a que a visita ou a proposta ficam ligadas.',
  leaveChat: 'Sair da conversa',
  leaveChatConfirm: 'Deixa de ver esta conversa. Se precisar, um colega volta a convidá-lo.',
  removeParticipant: 'Retirar da conversa',
  leftToast: 'Saiu da conversa.',
  participantsFailed: 'Não foi possível alterar os participantes.',
  openPropertyShort: 'Ficha do imóvel',
  openSiteShort: 'Ver no site',
  inviteShort: 'Convidar colega',
  transferShort: 'Transferir conversa',
  qualificationFreeText: 'Texto livre',
  segmentClients: 'Clientes',
  segmentStaff: 'Consultores',
  colleagueScopeLabel: 'Colegas de',
  colleagueScopeStore: 'A minha loja',
  colleagueScopeNetwork: 'Utilizadores da rede',
  colleagueStoreAll: 'Todas as agências',
  colleagueSearch: 'Procurar colega',
  colleagueEmpty: 'Nenhum colega ativo com chat neste âmbito.',
  newConversation: 'Nova conversa',
  newConversationTitle: 'Nova conversa',
  newConversationText: 'Escolha o colega. As conversas entre consultores são privadas, só entre os dois.',
  newConversationPlaceholder: 'Escolher colega',
  newConversationStart: 'Começar',
  emptyStaffTitle: 'Sem conversas com colegas',
  emptyStaffText: 'Use o + no topo da lista para começar uma conversa com um colega.',
  scheduleVisitTitle: 'Agendar visita',
  proposeSlots: 'Propor horários',
  proposeSlotsTitle: 'Propor horários ao cliente',
  visitPropertyLabel: 'Imóvel',
  changeProperty: 'Trocar',
  noVisitProperty: 'Esta conversa não tem imóvel. Escolha um.',
  quickPicksLabel: 'Sugestões',
  tomorrow: 'Amanhã',
  conflictWith: 'Sobrepõe-se a {items}. Pode marcar na mesma.',
  dateLabel: 'Data',
  timeLabel: 'Hora',
  durationLabel: 'Duração',
  minutesShort: 'min',
  sendClientEmail: 'Enviar confirmação por email ao cliente',
  slotsHint: 'Até 5 horários. O cliente escolhe um na conversa e a visita fica marcada, ou sugere outro.',
  pickerFilters: 'Filtros',
  pickerScopeLabel: 'Imóveis de',
  pickerMine: 'Os meus imóveis',
  pickerMyAgency: 'A minha agência',
  pickerNetwork: 'Toda a rede',
  pickerDistrict: 'Distrito',
  pickerCounty: 'Concelho',
  pickerCountyHint: 'Escolha primeiro um distrito',
  pickerPropertyType: 'Tipo de imóvel',
  pickerBusiness: 'Negócio',
  pickerTypology: 'Tipologia',
  pickerPriceMin: 'Preço mín.',
  pickerPriceMax: 'Preço máx.',
  pickerFromRequest: 'Tipos do pedido do cliente',
  pickerRemoveTag: 'Tirar este filtro',
  pickerClear: 'Limpar filtros',
  pickerOnlyAvailable: 'Só aparecem imóveis disponíveis e ativos.',
  pickerShowMore: 'Mostrar mais ({shown} de {total})',
  scheduleModeLabel: 'Como agendar',
  scheduleModeNow: 'Agendar agora',
  scheduleModeAsk: 'Perguntar disponibilidade',
  askAvailabilityNotice: 'O cliente escolhe um destes horários na conversa e a visita fica agendada automaticamente. Se nenhum servir, pode sugerir outro.',
  nextStepVisitForm: 'Visita agendada para {when}. Gere já a ficha de visita virtual.',
  addSlot: 'Outro horário',
  removeSlot: 'Tirar este horário',
  noteLabel: 'Mensagem para o cliente (opcional)',
  confirmSchedule: 'Marcar visita',
  sendRequest: 'Enviar pedido',
  visitScheduledToast: 'Visita marcada. O cliente recebeu a confirmação na conversa.',
  visitCanceledToast: 'Visita cancelada.',
  requestSentToast: 'Pedido enviado. Avisamos quando o cliente responder.',
  visitFailed: 'Não foi possível marcar a visita.',
  announceFailed: 'Não foi possível atualizar a conversa.',
  qualificationFailed: 'Não foi possível enviar a qualificação.',
  dealFailed: 'Não foi possível preparar o negócio desta conversa.',
  reminderFailed: 'Não foi possível criar o lembrete.',
  formFailed: 'Não foi possível enviar a avaliação da visita.',
  visitRequestTitle: 'Disponibilidade para visita',
  visitRequestText: 'Escolha o horário que lhe dá mais jeito:',
  visitRequestPending: 'À espera da resposta do cliente',
  visitRequestChosen: '{name} escolheu {when}',
  visitRequestProposed: '{name} propôs {when}',
  visitRequestDeclined: '{name} não tem disponibilidade nestes horários',
  visitRequestScheduled: 'Visita marcada',
  proposeOther: 'Propor outro horário',
  proposeConfirm: 'Propor',
  declineRequest: 'Sem disponibilidade',
  confirmVisitFromChoice: 'Confirmar visita',
  acceptProposal: 'Aceitar',
  scheduleAnyway: 'Marcar na mesma',
  visitRequestConflict: '{name} escolheu {when}, mas já tem algo na agenda a essa hora.',
  visitRequestScheduling: 'A marcar a visita…',
  visitCardTitle: 'Visita',
  visitStatus: { SCHEDULED: 'Marcada', DONE: 'Realizada', CANCELED: 'Cancelada' },
  visitCancel: 'Cancelar visita',
  visitCancelReason: 'Motivo do cancelamento',
  visitCancelConfirm: 'Cancelar visita',
  visitAddToCalendar: 'Adicionar ao calendário',
  react: 'Reagir',
  reactionFailed: 'Não foi possível alterar a mensagem.',
  voiceMessage: 'Mensagem de voz',
  recordingVoice: 'a gravar áudio…',
  recordVoice: 'Gravar mensagem de voz',
  recording: 'A gravar…',
  cancelRecording: 'Cancelar',
  sendRecording: 'Enviar gravação',
  micDenied: 'Sem acesso ao microfone. Verifique as permissões do browser.',
  voicePreview: '🎤 Mensagem de voz',
  voiceFailed: 'Não foi possível carregar o áudio.',
  play: 'Ouvir',
  pause: 'Pausar',
  playbackSpeed: 'Velocidade',
  deleteTitle: 'Apagar mensagem',
  deleteChoiceText: 'Para todos: desaparece para toda a gente na conversa. Para mim: só deixa de aparecer aqui.',
  deleteForMeText: 'A mensagem deixa de aparecer para si. Os outros continuam a vê-la.',
  deleteForEveryone: 'Para todos',
  deleteForMe: 'Para mim',
  scheduleSend: 'Enviar mais tarde',
  scheduleTitle: 'Enviar quando?',
  scheduleConfirm: 'Agendar',
  scheduleCustom: 'Outra data e hora',
  scheduledCount: '{n} mensagens por enviar',
  scheduledOne: '1 mensagem por enviar',
  scheduledAt: 'Sai {when}',
  cancelScheduled: 'Cancelar envio',
  scheduledToast: 'Mensagem agendada. Sai {when}.',
  scheduledCanceledToast: 'Envio cancelado.',
  scheduledFailed: 'Uma mensagem agendada não foi enviada.',
  scheduleFailed: 'Não foi possível agendar a mensagem.',
  nextStepConfirmVisit: 'O cliente escolheu {when}.',
  nextStepAcceptProposal: 'O cliente propôs {when}.',
  nextStepMatching: '{n} imóveis encaixam no que o cliente procura.',
  nextStepMatchingOne: '1 imóvel encaixa no que o cliente procura.',
  nextStepAnswered: 'O cliente respondeu à qualificação.',
  nextStepQualify: 'Ainda não sabe o que o cliente procura.',
  nextStepPropose: 'Já enviou imóveis. Proponha horários para uma visita.',
  nextStepEvaluate: 'A visita de {when} já aconteceu.',
  nextStepProposal: 'O cliente gostou da visita ({n}/5).',
  nextStepSeeAndSend: 'Ver e enviar',
  nextStepSendProperties: 'Enviar imóveis',
  nextStepDismiss: 'Agora não',
  rescheduleVisit: 'Reagendar',
  rescheduleVisitTitle: 'Reagendar visita',
  confirmReschedule: 'Guardar novo horário',
  visitRescheduledToast: 'Visita reagendada. O cliente vê o novo horário na conversa.',
  visitRescheduled: 'Reagendada',
  visitRequestForm: 'Ficha de visita',
  visitFormTitle: 'Ficha de visita',
  visitFormPending: 'À espera da resposta do cliente',
  visitFormAnswered: 'Respondida pelo cliente',
  visitFormOpen: 'Abrir avaliação',
  visitFormCopyLink: 'Copiar link',
  linkCopied: 'Link copiado.',
  requestFormTitle: 'Ficha de visita virtual',
  requestFormText: 'Escolha a visita. O cliente recebe a ficha na conversa, preenche e rubrica no telemóvel.',
  requestFormSend: 'Enviar ficha',
  noVisits: 'Ainda não há visitas realizadas nesta conversa.',
  formRequested: 'Avaliação enviada',
  formAnsweredShort: 'Avaliação respondida',
  formSentToast: 'Ficha de visita enviada ao cliente.',
  qualify: 'Qualificar',
  qualificationTitle: 'Queremos saber mais sobre si',
  qualificationText: 'Responda em segundos e encontramos o imóvel certo para si.',
  qualificationPending: 'À espera da resposta do cliente',
  qualificationAnswered: 'Respondido por {name}',
  qualificationSend: 'Enviar perguntas',
  qualificationFieldsHint: 'Escolha o que quer perguntar. O cliente responde na conversa.',
  qualificationField: {
    propertyType: 'Tipo de imóvel',
    businessType: 'Comprar ou arrendar',
    budget: 'Orçamento',
    bedrooms: 'Quartos',
    notes: 'Mais detalhes'
  },
  qualificationEdit: 'Editar',
  qualificationSearch: 'Procurar imóveis',
  qualificationSave: 'Guardar',
  qualificationSentToast: 'Perguntas enviadas ao cliente.',
  qualificationSavedToast: 'Qualificação guardada.',
  budgetMinLabel: 'Mínimo (€)',
  budgetMaxLabel: 'Máximo (€)',
  bedroomsLabel: 'Quartos',
  notesLabel: 'Mais detalhes',
  notesPlaceholder: 'Zona, garagem, elevador, prazo…',
  propertyTypeLabel: 'Tipo de imóvel',
  propertyTypePlaceholder: 'Qualquer tipo',
  businessTypeLabel: 'Comprar ou arrendar',
  qualAnswerForClient: 'Responder pelo cliente',
  qualEditAnswers: 'Editar respostas',
  qualAnswerMyself: 'Responder eu',
  qualPreviewHint: 'O cliente vê as perguntas na conversa e responde uma de cada vez. Toque numa para a tirar.',
  qualPreviewEmpty: 'Escolha pelo menos uma pergunta.',
  qualPreviewByField: {
    propertyType: 'apartamento, moradia, terreno…',
    businessType: 'comprar ou arrendar',
    budget: 'de … até … €',
    bedrooms: 'T0 a T5+',
    notes: 'texto livre'
  },
  qualAnswersHint: 'Toque nas opções, só no que souber. Fica guardado nas preferências do cliente.',
  budgetUpTo: 'até {value}',
  proposalCardTitle: 'Proposta',
  openProposal: 'Ver no negócio',
  proposalSharedToast: 'Proposta registada na conversa.',
  proposalNotFound: 'Não encontrámos uma proposta nova desta lead. Registe-a e volte a tentar.',
  advanceStageTo: 'Avançar para {name}',
  stageAdvancedToast: 'Negócio avançou de etapa.',
  stageFailed: 'Não foi possível mudar a etapa.',
  nextActionLabel: 'Próxima ação',
  noNextAction: 'Sem próxima ação marcada.',
  addReminder: 'Lembrete',
  reminderTitle: 'Lembrar-me',
  reminderNote: 'O que fazer',
  reminderSave: 'Criar lembrete',
  reminderSavedToast: 'Lembrete criado no seu calendário.',
  nextActionVisit: 'Visita marcada',
  transferTitle: 'Transferir conversa',
  transferText: 'A conversa passa para outro consultor, que fica responsável e recebe a sua nota de contexto.',
  transferPlaceholder: 'Escolher consultor',
  transferNote: 'Nota de contexto para o colega (só a equipa vê)',
  transferConfirm: 'Transferir',
  transferFailed: 'Não foi possível transferir a conversa.',
  transferredToast: 'Conversa transferida.',
  mentionsLabel: 'Mencionados',
  previewByKind: {
    property: 'Imóvel enviado',
    visit_request: 'Pedido de disponibilidade',
    visit: 'Visita',
    visit_form: 'Avaliação da visita',
    qualification: 'Qualificação',
    proposal: 'Proposta',
    note: 'Nota interna',
    system: 'Atualização da conversa'
  },
  roleLabel: 'Papel',
  responsibleLabel: 'Responsável',
  propertyLabel: 'Imóvel',
  stateLabel: 'Estado',
  colleaguesTitle: 'Colegas na conversa',
  participantResponsible: 'Responsável',
  participantInvited: 'Convidado',
  participantYou: 'Você',
  inviteColleague: 'Convidar colega',
  conversationPropertyTitle: 'Imóvel da conversa',
  sentPropertiesTitle: 'Imóveis enviados',
  noColleagues: 'Ainda ninguém da equipa nesta conversa.',
  noteAddedBy: 'Adicionada por {name}',
  noteVisibility: 'Nota interna · visível apenas para a equipa',
  seeDetails: 'Ver detalhes',
  priceOnRequest: 'Preço sob consulta',
  forSale: 'Venda',
  forRent: 'Arrendamento',
  bathroomsShort: '{n} WC',
  propertyStatus: {
    AVAILABLE: 'Disponível',
    RESERVED: 'Reservado',
    SOLD: 'Vendido',
    RENTED: 'Arrendado',
    TO_VALIDATE: 'Por validar',
    PROSPECTING: 'Em prospeção',
    DECLINED: 'Recusado',
    REMOVED: 'Retirado'
  },
  myStatus: 'O meu estado',
  presenceAvailableHint: 'Os clientes veem-no online',
  presenceAwayHint: 'Responde mais tarde; a resposta automática de ausência sai por si',
  launcherNewConversation: 'Nova conversa',
  launcherPeekOpen: 'Abrir conversa',
  launcherPeekDismiss: 'Dispensar aviso',
  editMessage: 'Editar',
  editingMessage: 'A editar mensagem',
  cancelEdit: 'Cancelar edição',
  edited: 'Editada',
  editExpired: 'Esta mensagem já não pode ser editada: só é possível nos 15 minutos depois de a enviar.',
  editFailed: 'Não foi possível editar a mensagem.',
  messageDeleted: 'Esta mensagem foi apagada',
  messageDeletedMine: 'Apagou esta mensagem',
  noteDeleted: 'Esta nota foi apagada',
  messageDeletedPreview: 'Mensagem apagada',
  deleteForEveryoneTitle: 'Apagar para todos?',
  deleteForEveryoneText: 'A mensagem desaparece para todos na conversa e fica "Esta mensagem foi apagada" no lugar dela.',
  deleteExpired: 'Esta mensagem já não pode ser apagada para todos: só é possível até 2 dias e 12 horas depois de a enviar.',
  reply: 'Responder',
  replyingTo: 'A responder a {name}',
  cancelReply: 'Cancelar resposta',
  replyQuoteHint: 'Ir para a mensagem citada',
};
